import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { handleApiError, shouldLogout, logError } from './errorHandler';

/**
 * Base API URL from environment variables
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error('VITE_API_BASE_URL environment variable is not defined. Please set it in your .env file.');
}

/**
 * Create axios instance with default configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Token Refresh Management
// ============================================================================

/**
 * State for managing token refresh
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string | PromiseLike<string>) => void;
  reject: (reason?: any) => void;
}> = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: any, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string | null => {
  try {
    const authState = localStorage.getItem('auth-storage');
    if (authState) {
      const parsed = JSON.parse(authState);
      return parsed.state?.token || null;
    }
  } catch (error) {
    console.error('Failed to get auth token:', error);
  }
  return null;
};

/**
 * Get refresh token from localStorage
 */
const getRefreshToken = (): string | null => {
  try {
    const authState = localStorage.getItem('auth-storage');
    if (authState) {
      const parsed = JSON.parse(authState);
      return parsed.state?.refreshToken || null;
    }
  } catch (error) {
    console.error('Failed to get refresh token:', error);
  }
  return null;
};

/**
 * Update tokens in localStorage (used after successful refresh)
 */
const updateStoredTokens = (accessToken: string, expiresIn: number): void => {
  try {
    const authState = localStorage.getItem('auth-storage');
    if (authState) {
      const parsed = JSON.parse(authState);
      parsed.state.token = accessToken;
      parsed.state.tokenExpiresAt = Date.now() + expiresIn * 1000;
      localStorage.setItem('auth-storage', JSON.stringify(parsed));
      console.log('[API] Tokens updated in localStorage');
    }
  } catch (error) {
    console.error('[API] Failed to update stored tokens:', error);
  }
};

/**
 * Clear authentication and redirect to login
 */
const handleUnauthorized = (): void => {
  // Clear auth state from localStorage
  localStorage.removeItem('auth-storage');

  // Dispatch custom event for auth store to react
  window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'unauthorized' } }));

  console.warn('[API] Unauthorized access - token cleared');
};

/**
 * Attempt to refresh the access token
 * Returns the new access token or throws an error
 */
const attemptTokenRefresh = async (): Promise<string> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    console.warn('[API] No refresh token available');
    throw new Error('No refresh token available');
  }

  console.log('[API] Attempting token refresh...');

  try {
    // Use axios directly to avoid interceptors during refresh
    const response = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const { access_token, expires_in } = response.data;

    // Update stored tokens
    updateStoredTokens(access_token, expires_in);

    console.log('[API] Token refresh successful');
    return access_token;
  } catch (error) {
    console.error('[API] Token refresh failed:', error);
    throw error;
  }
};

/**
 * Request Interceptor
 * - Adds authentication token to requests
 * - Logs outgoing requests in development
 */
apiClient.interceptors.request.use(
  (config) => {
    // Add authentication token if available
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data,
      });
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * - Handles successful responses
 * - Handles 401 errors with automatic token refresh
 * - Queues concurrent requests during refresh
 * - Handles other errors (network errors, etc.)
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const appError = handleApiError(error);

    // Log error
    logError(appError, `${error.config?.method?.toUpperCase()} ${error.config?.url}`);

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Don't try to refresh if the failing request was the refresh endpoint itself
      if (originalRequest.url?.includes('/auth/refresh')) {
        console.warn('[API] Refresh token endpoint returned 401 - logging out');
        handleUnauthorized();
        return Promise.reject(error);
      }

      // Don't try to refresh for login/register/social-login endpoints
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/auth/social-login')
      ) {
        return Promise.reject(error);
      }

      // Check if we have a refresh token before attempting refresh
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        console.warn('[API] No refresh token available - logging out');
        handleUnauthorized();
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        console.log('[API] Token refresh in progress, queueing request:', originalRequest.url);
        return new Promise<AxiosResponse>((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              apiClient(originalRequest).then(resolve, reject);
            },
            reject: (err: any) => {
              reject(err);
            },
          });
        });
      }

      // Mark as retrying and start refresh
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await attemptTokenRefresh();

        // Process queued requests with new token
        processQueue(null, newToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - process queue with error and logout
        processQueue(refreshError, null);
        handleUnauthorized();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other 401 cases (after failed refresh or other scenarios)
    if (shouldLogout(appError)) {
      handleUnauthorized();
    }

    // Queue failed requests for retry (handled by offline queue service if needed)
    if (appError.isRetryable && error.config) {
      // Dispatch event for offline queue to handle
      window.dispatchEvent(
        new CustomEvent('api:failed-request', {
          detail: {
            config: error.config,
            error: appError,
          },
        })
      );
    }

    return Promise.reject(error);
  }
);

/**
 * Generic API methods
 */
export const api = {
  /**
   * GET request
   */
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.get<T>(url, config);
  },

  /**
   * POST request
   */
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.post<T>(url, data, config);
  },

  /**
   * PUT request
   */
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.put<T>(url, data, config);
  },

  /**
   * PATCH request
   */
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.patch<T>(url, data, config);
  },

  /**
   * DELETE request
   */
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.delete<T>(url, config);
  },
};

/**
 * API endpoints
 */
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    PROFILE: '/auth/profile',
    UPDATE_PROFILE: '/auth/profile',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    SEND_OTP: '/auth/otp/send',
    SEND_EMAIL_OTP: '/auth/email/send-otp',
    VERIFY_EMAIL_OTP: '/auth/email/verify-otp',
    CHANGE_PASSWORD: '/auth/change-password',
    REQUEST_PASSWORD_RESET: '/auth/password-reset/request',
    CONFIRM_PASSWORD_RESET: '/auth/password-reset/confirm',
    SOCIAL_LOGIN: '/auth/social-login',
    COLLECT_PHONE: '/auth/profile/phone',
    CHECK_PHONE: '/auth/phone/check',
    CHECK_EMAIL: '/auth/email/check',
  },

  // Live status
  LIVE: {
    LOCATIONS: '/live/locations',
    CHARGE_POINT: (id: string) => `/live/charge-points/${id}`,
    TRANSACTION: (id: string) => `/live/transactions/${id}`,
  },

  // Locations
  LOCATIONS: {
    BASE: '/locations',
    NEARBY: '/locations/nearby',
    SEARCH: '/locations/search',
    BY_ID: (id: string) => `/locations/${id}`,
    COMPATIBLE: '/locations/compatible',
    STATUS_SUMMARY: '/locations/status-summary',
  },

  // Favorites
  FAVORITES: {
    BASE: '/users/me/favorites',
    BY_ID: (locationId: string) => `/users/me/favorites/${locationId}`,
  },

  // ChargePoints
  CHARGE_POINTS: {
    BY_LOCATION: (locationId: string) => `/charge-points/location/${locationId}`,
    BY_ID: (id: string) => `/charge-points/${id}`,
    BY_CHARGER_ID: (chargerId: string) => `/charge-points/by-charger-id/${encodeURIComponent(chargerId)}`,
    EVSES: (chargePointId: string) => `/charge-points/${chargePointId}/evses`,
    STATUS: (id: string) => `/charge-points/${id}/status`,
  },

  // Tariffs
  TARIFFS: {
    RESOLVE: (evseId: string) => `/tariffs/resolve?evseId=${evseId}`,
  },

  // Transactions
  TRANSACTIONS: {
    BASE: '/transactions',
    MY_TRANSACTIONS: '/transactions/my-transactions',
    START: '/transactions/start',
    STOP: '/transactions/stop',
    BY_ID: (id: string) => `/transactions/${id}`,
  },

  // Wallet
  WALLET: {
    BASE: '/wallet',
    BALANCE: '/wallet',
    TRANSACTIONS: '/wallet/transactions',
    HISTORY: '/wallet/history',
    RECHARGE: '/wallet/recharge/initiate',
    VERIFY_PAYMENT: '/wallet/recharge/verify',
  },

  // Invoices
  INVOICES: {
    BY_TRANSACTION_ID: (transactionId: string) => `/invoices/by-transaction/${transactionId}`,
    GENERATE_FROM_TRANSACTION: '/invoices/generate-from-transaction',
    BY_WALLET_TRANSACTION_ID: (walletTransactionId: string) => `/invoices/wallet-transaction/${walletTransactionId}`,
    GENERATE_FROM_RECHARGE: '/invoices/generate-from-recharge',
  },

  // App Config
  APP_CONFIG: '/app-config',

  // Vehicles
  VEHICLES: {
    BASE: '/vehicles',
    MY_VEHICLES: '/vehicles/my-vehicles',
    BY_ID: (id: string) => `/vehicles/${id}`,
    SET_PRIMARY: (id: string) => `/vehicles/${id}/set-primary`,
    REFERENCE_DATA: '/vehicles/reference-data',
  },
};

/**
 * Export axios instance for custom use cases
 */
export default apiClient;
